import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { IAlert } from '../../types/entities/IAlert';
import { FeedbackMessages } from '../../utils/commonVariables/messages';
import { ClientEvents } from '../../utils/commonVariables/signalR';
import { playAlertSound } from '../../utils/playAlertSound';

const retryDelayMilliseconds = 10 * 60 * 1000;

type SharedConnectionMessage =
    | { type: 'alert'; alert: IAlert }
    | { type: 'resolved'; ids: string[] }
    | { type: 'status'; connected: boolean; failed: boolean }
    | { type: 'request-status' };

interface SharedConnectionOptions {
    endpoint: string;
    webToken: string | null;
    sid: string | null;
    isCurrent: () => boolean;
    onAlert: (alert: IAlert) => void;
    onResolved: (ids: string[]) => void;
    onStatus: (connected: boolean) => void;
    onError: (message: string) => void;
}

/**
 * Owns leader election, hub retries and cross-tab messages without React state.
 * @param options Session credentials and event handlers.
 * @returns Confirmed-resolution broadcasting and connection cleanup.
 */
export const createSharedSignalRConnection = ({
    endpoint,
    webToken,
    sid,
    isCurrent,
    onAlert,
    onResolved,
    onStatus,
    onError,
}: SharedConnectionOptions) => {
    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let hasReportedError = false;
    let leaderConnected = false;
    let isLeader = false;
    let hub: HubConnection | undefined;
    let channel: BroadcastChannel | undefined;
    let releaseLeadership: (() => void) | undefined;
    const lockAbortController = new AbortController();
    const isActive = () => !disposed && isCurrent();
    const broadcast = (message: SharedConnectionMessage) => {
        if (isActive()) {
            channel?.postMessage(message);
        }
    };
    const publishStatus = (connected: boolean, failed = false) => {
        leaderConnected = connected;
        onStatus(connected);
        broadcast({ type: 'status', connected, failed });
    };
    const reportConnectionError = () => {
        if (!hasReportedError) {
            onError(FeedbackMessages.SIGNALR_CONNECTION_ERROR);
            hasReportedError = true;
        }
    };
    const handleAlert = (alert: IAlert) => {
        if (isActive()) {
            if (!alert.Resolved) {
                playAlertSound();
            }
            onAlert(alert);
            broadcast({ type: 'alert', alert });
        }
    };
    const scheduleRetry = () => {
        if (isActive()) {
            clearTimeout(retryTimer);
            retryTimer = setTimeout(() => {
                void start();
            }, retryDelayMilliseconds);
        }
    };
    const start = async () => {
        if (!hub || !isActive()) {
            return;
        }
        try {
            await hub.start();
            if (isActive()) {
                hasReportedError = false;
                publishStatus(true);
            }
        } catch {
            if (isActive()) {
                reportConnectionError();
                publishStatus(false, true);
                scheduleRetry();
            }
        }
    };
    const startLeader = () => {
        isLeader = true;
        publishStatus(false);
        try {
            hub = new HubConnectionBuilder()
                .withUrl(endpoint, {
                    accessTokenFactory: () => webToken || '',
                })
                .withAutomaticReconnect()
                .build();
        } catch {
            onError(FeedbackMessages.SIGNALR_CONFIGURATION_ERROR);
            publishStatus(false, true);
            isLeader = false;
            releaseLeadership?.();
            return;
        }
        hub.on(ClientEvents.ALERT, handleAlert);
        hub.onreconnecting(() => {
            if (isActive()) {
                publishStatus(false);
            }
        });
        hub.onreconnected(() => {
            if (isActive()) {
                hasReportedError = false;
                publishStatus(true);
            }
        });
        hub.onclose(() => {
            if (isActive()) {
                reportConnectionError();
                publishStatus(false, true);
                scheduleRetry();
            }
        });
        void start();
    };

    if (typeof BroadcastChannel !== 'undefined' && navigator.locks) {
        const channelName = `deatheaters-signalr:${endpoint}:${sid || ''}`;
        try {
            channel = new BroadcastChannel(channelName);
            channel.onmessage = ({
                data,
            }: MessageEvent<SharedConnectionMessage>) => {
                if (!isActive()) {
                    return;
                }
                if (data.type === 'resolved') {
                    onResolved(data.ids);
                } else if (data.type === 'request-status' && isLeader) {
                    broadcast({
                        type: 'status',
                        connected: leaderConnected,
                        failed: hasReportedError,
                    });
                } else if (data.type === 'status' && !isLeader) {
                    onStatus(data.connected);
                    if (data.failed) {
                        reportConnectionError();
                    } else if (data.connected) {
                        hasReportedError = false;
                    }
                } else if (data.type === 'alert' && !isLeader) {
                    onAlert(data.alert);
                }
            };
            broadcast({ type: 'request-status' });
            void navigator.locks
                .request(
                    channelName,
                    { signal: lockAbortController.signal },
                    async () => {
                        if (isActive()) {
                            await new Promise<void>(resolve => {
                                releaseLeadership = resolve;
                                startLeader();
                            });
                        }
                    }
                )
                .catch(() => {
                    if (isActive()) {
                        onError(FeedbackMessages.SIGNALR_CONFIGURATION_ERROR);
                    }
                });
        } catch {
            onError(FeedbackMessages.SIGNALR_CONFIGURATION_ERROR);
            channel?.close();
            channel = undefined;
        }
    } else {
        startLeader();
    }

    return {
        broadcastResolved: (ids: string[]) =>
            broadcast({ type: 'resolved', ids }),
        dispose: () => {
            if (isLeader) {
                broadcast({ type: 'status', connected: false, failed: false });
            }
            disposed = true;
            lockAbortController.abort();
            clearTimeout(retryTimer);
            channel?.close();
            if (hub) {
                hub.off(ClientEvents.ALERT, handleAlert);
                // Release the lock only after the previous hub has stopped.
                void hub
                    .stop()
                    .catch(() => {
                        onError(FeedbackMessages.SIGNALR_CONNECTION_ERROR);
                    })
                    .finally(() => releaseLeadership?.());
            } else {
                releaseLeadership?.();
            }
        },
    };
};
